import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, HashRouter } from 'react-router-dom';
import App from './App';
import { isElectron } from './helpers/EnvironmentFlags/EnvironmentFlags';
import './index.css';
import reportWebVitals from './reportWebVitals';
import * as serviceWorker from './serviceWorker';
import * as Sentry from '@sentry/react';
import * as Integrations from '@sentry/integrations';
import { versionNumber } from './helpers/version';
import store from 'store2';
import { isDevelopment } from './helpers/EnvironmentFlags/EnvironmentFlags';

if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_WDYR === 'true') {
    import('@welldone-software/why-did-you-render').then((module) => {
        module.default(React, {
            trackAllPureComponents: true,
            collapseGroups: true,
            trackHooks: true,
        });
    });
}

Sentry.init({
    dsn: 'https://99bea274eda94405a9b0b5d72a3cf893@o76416.ingest.sentry.io/6542040',
    denyUrls: [
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
    ],
    integrations: [
        // send an event to sentry whenever an error is logged in the console
        new Integrations.CaptureConsole({ levels: ['error'] }),
        // send an event to sentry when an error or an unhandled webservice rejection occurs
        new Sentry.Integrations.GlobalHandlers({
            onerror: true,
            onunhandledrejection: true,
        }),
        // Used in conjunction with normalizeDepth to allow objects in the 'extra' field to have a depth of 10
        new Integrations.ExtraErrorData({ depth: 10 }),
    ],
    // limits all objects sent in an error event to a depth of 10
    normalizeDepth: 10,
    attachStacktrace: true,
    beforeSend: (event: Sentry.Event, hint: Sentry.EventHint) => {
        const originalExceptionText = hint?.originalException?.toString() ?? '';
        if (isDevelopment() || [
            'Invocation canceled due to the underlying connection being closed.',
            'Server timeout elapsed without receiving a message from the server.',
            'Track has ended',
            'A receipt transaction is still in progress',
        ].some((message: string) => originalExceptionText.includes(message)) || [
            'Error: Failed to start the HttpConnection before stop() was called.',
            'Error: Failed to start the connection',
            'Error: Connection disconnected with error',
            'Error: Failed to complete negotiation with the server',
            'Error activating scanner',
            'Invalid call to runtime.sendMessage(). Tab not found.',
        ].some((message: string) => event.message?.includes(message) || originalExceptionText.includes(message))) {
            return null;
        }

        event.tags = event.tags || {};
        event.tags['kiosk'] = store.session.get('kiosk') ?? 'N/A';

        return event;
    },
    environment: process.env.REACT_APP_GOMOTO_API_USERNAME?.toLowerCase(),
});

Sentry.configureScope((scope) => {
    scope.setTags({
        version: versionNumber(),
        application: isElectron() ? 'Kiosk' : 'Mobile Check In',
    });
});

export const Router: React.FC<React.PropsWithChildren<unknown>> = (props) => {
    return (
        isElectron() ? (
            <HashRouter basename={'/'} {...props} />
        ) : (
            <BrowserRouter basename={'/'} {...props} />
        ));
};

const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement,
);


root.render(
    <Router>
        {/* TODO re-enabled
            <React.StrictMode>
                <App />
            </React.StrictMode>*/
        }
        <App />
    </Router>,
);

/*
 * If you want your app to work offline and load faster, you can change
 * unregister() to register() below. Note this comes with some pitfalls.
 * Learn more about service workers: https://bit.ly/CRA-PWA
 */
serviceWorker.unregister();

/*
 * If you want to start measuring performance in your app, pass a function
 * to log results (for example: reportWebVitals(console.log))
 * or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
 */
process.env.REACT_APP_WEB_VITALS === 'true' && reportWebVitals(console.log);
